import { useOnClickOutside } from '@/hooks'
import { useRef, useCallback } from 'react'

/**
 * Returns refs for dropdown and button, and sets up outside click handler
 * that ignores clicks on the button.
 *
 * Usage:
 *   const { dropdownRef, buttonRef } = useDropdownClickOutside(isOpen, () => setIsOpen(false))
 *   <button ref={buttonRef} ... />
 *   <div ref={dropdownRef}>...</div>
 */
export function useDropdownClickOutside(onClickOutside: () => void) {
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const shouldHandle = useCallback((event: MouseEvent) => {
        if (buttonRef.current?.contains(event.target as Node)) {
            return false
        }
        return true
    }, [])

    useOnClickOutside(
        dropdownRef,
        () => {
            onClickOutside()
        },
        shouldHandle
    )

    return { dropdownRef, buttonRef }
}
